import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  HubSection,
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  AskAboutThis,
  directionInfo,
  actionHint,
} from "@/components/research-hub";
import {
  usePmPositioning,
  usePmHouseView,
  type PositioningRow,
  type PmHouseViewRow,
} from "@/hooks/useResearchHubPm";

const HUB = "/partner/research-hub";

type Row = Record<string, any>;

// A small direction glyph (▲ bullish / ▼ bearish) for inline use before a subject.
function dirPrefix(dir: string | number | null | undefined) {
  const d = directionInfo(dir);
  if (!d.glyph) return null;
  return (
    <span className="text-muted-foreground" aria-label={d.word || undefined}>
      {d.glyph}
    </span>
  );
}

// "how relevant to your mandate" from geo_compat (0..1) — never a raw decimal.
function relevanceText(geoCompat: number | null | undefined): string {
  if (typeof geoCompat !== "number" || !Number.isFinite(geoCompat)) return "relevance unknown";
  if (geoCompat >= 1) return "fully relevant";
  if (geoCompat <= 0) return "not relevant to your mandate";
  const p = Math.round(geoCompat * 100);
  if (p === 0) return "barely relevant"; // a tiny but non-zero score shouldn't read as "0% relevant"
  return `${p}% relevant`;
}

export default function PmPositioning() {
  const positioning = usePmPositioning();
  const houseView = usePmHouseView();
  const navigate = useNavigate();

  return (
    <div className="space-y-8 max-w-3xl">
      <p className="font-heading text-xl">Your positioning</p>

      {/* Section 1 — Your positioning (what to act on) */}
      <HubSection
        title="Your positioning"
        description="Where your book sits against the house view, and what's pushing back."
      >
        {positioning.isLoading ? (
          <LoadingState rows={3} />
        ) : positioning.error ? (
          <ErrorState onRetry={() => positioning.refetch()} />
        ) : (positioning.data ?? []).length === 0 ? (
          <EmptyState title="Nothing stands out in your positioning right now." />
        ) : (
          <div className="space-y-3">
            {positioning.data!.map((r: PositioningRow, i: number) => (
              <PositioningCard
                key={r.thesis_edge_id ?? i}
                row={r}
                onOpen={(id) => navigate(`${HUB}/thesis/${id}`)}
              />
            ))}
          </div>
        )}
      </HubSection>

      {/* Section 2 — The house view behind your book (quiet context) */}
      <HubSection
        title="The house view behind your book"
        description="Standing views that touch your mandate. Context, not an action list."
      >
        {houseView.isLoading ? (
          <LoadingState rows={2} />
        ) : houseView.error ? (
          <ErrorState onRetry={() => houseView.refetch()} />
        ) : (houseView.data ?? []).length === 0 ? (
          <EmptyState title="No standing house views match your mandate yet." />
        ) : (
          <div className="space-y-2">
            {houseView.data!.map((h: PmHouseViewRow, i: number) => (
              <HouseViewRow
                key={h.thesis_edge_id ?? i}
                row={h}
                onOpen={(id) => navigate(`${HUB}/thesis/${id}`)}
              />
            ))}
          </div>
        )}
      </HubSection>
    </div>
  );
}

function PositioningCard({
  row,
  onOpen,
}: {
  row: PositioningRow;
  onOpen: (id: string) => void;
}) {
  const subject = row.subject_label ?? "A position";
  const signed = typeof row.signed_conviction === "number" ? row.signed_conviction : undefined;
  // Conviction is shown as a bar only — never a decimal. Magnitude carries strength.
  const convictionValue = Math.abs(signed ?? row.conviction_score ?? 0);
  const action = actionHint(row.action_hint);

  const fundCount = typeof row.reached_fund_count === "number" ? row.reached_fund_count : null;
  const disconfirmer: Row = row.top_disconfirmer ?? {};
  // top_disconfirmer is opaque Json — only render the quote when it's actually a non-empty string, so
  // an object/number never interpolates as "[object Object]".
  const rawQuote = disconfirmer.quote ?? disconfirmer.evidence_quote;
  const counterQuote = typeof rawQuote === "string" && rawQuote.trim() ? rawQuote.trim() : null;
  const divergenceIds = Array.isArray(row.open_divergence_alert_ids)
    ? row.open_divergence_alert_ids
    : [];

  const prefill = `What's our positioning on ${subject}, and what's the strongest case against it?`;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div
          className="min-w-0 flex-1 cursor-pointer"
          onClick={() => row.thesis_edge_id && onOpen(row.thesis_edge_id)}
        >
          <div className="font-heading text-base font-medium truncate">
            {dirPrefix(signed)} <span className="align-middle">{subject}</span>
          </div>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <StrengthBar value={convictionValue} />
            {row.single_source_risk && (
              <span className="font-body text-[11px] text-muted-foreground">one source</span>
            )}
            {fundCount != null && (
              <span className="font-body text-[11px] text-muted-foreground">
                {fundCount} of your funds
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Qualitative pill — no sizes implied anywhere. */}
          <AttentionBadge label={action.label} tone={action.tone} />
          <AskAboutThis prefill={prefill} label="" />
        </div>
      </div>

      {divergenceIds.length > 0 && (
        <AttentionBadge label="the market has diverged" tone="bad" />
      )}

      {counterQuote && (
        <div className="rounded-md border bg-muted/40 p-3 space-y-2">
          <p className="font-body text-sm italic text-foreground/90">"{counterQuote}"</p>
          <StrengthBar value={row.disconfirmer_strength} label="case against" />
        </div>
      )}
    </Card>
  );
}

function HouseViewRow({
  row,
  onOpen,
}: {
  row: PmHouseViewRow;
  onOpen: (id: string) => void;
}) {
  const subject = row.subject_label ?? "A house view";
  return (
    <Card
      className="p-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/50"
      onClick={() => row.thesis_edge_id && onOpen(row.thesis_edge_id)}
    >
      <div className="min-w-0">
        <div className="font-body text-sm truncate">
          {dirPrefix(row.thesis_direction)} <span className="align-middle">{subject}</span>
        </div>
        <div className="font-body text-xs text-muted-foreground">
          {relevanceText(row.geo_compat)}
          {row.signal_label ? ` · ${row.signal_label}` : ""}
        </div>
      </div>
    </Card>
  );
}
